import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Endpoints whose own 401 is meaningful to the caller (wrong password, expired
// session probe) — the interceptor must NOT try to refresh/retry/redirect on
// these, or it would mask the LoginPage error and loop on the refresh call.
const AUTH_PATHS = ["/api/auth/login", "/api/auth/refresh", "/api/auth/logout", "/api/auth/me"];

// Route prefixes served to people WITHOUT a login (token/public pages). A 401
// here must never bounce the visitor to /login — they have no account.
const PUBLIC_ROUTE_PREFIXES = [
  "/form/", "/sign/", "/p/", "/s/", "/privacy-training/", "/change-details", "/new", "/intake/student",
];

function isPublicRoute() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return PUBLIC_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(p));
}

function isAuthPath(url = "") {
  return AUTH_PATHS.some((p) => url.includes(p));
}

// Single in-flight refresh shared across all concurrent 401s, so a burst of
// failed requests triggers exactly one /api/auth/refresh, then all retry.
let refreshPromise = null;
function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API}/api/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

let installed = false;

// Installs a 401 response interceptor on the given axios instance. On a 401 for
// a normal (non-auth) request it refreshes the session once and retries the
// original request; if the refresh fails and the user is on a protected page,
// it redirects to /login. Idempotent for the shared default instance.
export function installAuthInterceptors(instance = axios) {
  if (instance === axios) {
    if (installed) return;
    installed = true;
  }
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config || {};
      const url = original?.url || "";

      if (status !== 401 || isAuthPath(url) || original._retriedAfterRefresh) {
        return Promise.reject(error);
      }

      try {
        await refreshOnce();
      } catch {
        // Refresh failed → session is really gone. Only send logged-in users to
        // /login; public token visitors stay where they are.
        if (!isPublicRoute() && typeof window !== "undefined" && window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
        return Promise.reject(error);
      }

      // Refresh succeeded — replay the original request exactly once.
      original._retriedAfterRefresh = true;
      return instance(original);
    }
  );
}
